import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material'
import { Close, ArrowForward, ArrowBack, Check } from '@mui/icons-material'
import { useCreateVibesMutation } from 'graphql/generated/graphql'
import { errorMessage, successMessage } from 'components/toast/toast'

interface TrainingPost {
  id: number
  imageUrl: string
  username: string
  caption: string
  category: string
}

// Curated sample posts across different niches
const TRAINING_POSTS: TrainingPost[] = [
  {
    id: 1,
    imageUrl: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=400&fit=crop',
    username: 'fitlifemike',
    caption: 'Finally hit my PR on deadlift after 6 months of grinding. 315lbs felt light today 💪 Consistency really is everything.',
    category: 'Fitness',
  },
  {
    id: 2,
    imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    username: 'wanderlust.sarah',
    caption: 'Woke up at 4am for this sunrise in Bali and it was absolutely worth it. Some moments just hit different ✨',
    category: 'Travel',
  },
  {
    id: 3,
    imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=400&fit=crop',
    username: 'homechef_anna',
    caption: 'Made this from scratch for the first time! Not perfect but honestly proud of how it turned out. What should I try next?',
    category: 'Food',
  },
  {
    id: 4,
    imageUrl: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=400&fit=crop',
    username: 'startupjourney',
    caption: 'Year 2 of building my business. Still scary, still uncertain, but wouldn\'t trade it for anything. Here\'s to betting on yourself.',
    category: 'Business',
  },
  {
    id: 5,
    imageUrl: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=400&fit=crop',
    username: 'mindful.living',
    caption: 'Started meditating 30 days ago as an experiment. Honestly didn\'t expect much but the mental clarity has been real. Anyone else notice this?',
    category: 'Wellness',
  },
  {
    id: 6,
    imageUrl: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop',
    username: 'creative_beats',
    caption: 'New track dropping Friday. Been working on this one for months - it\'s my most personal project yet. Nervous but excited to share.',
    category: 'Creative',
  },
  {
    id: 7,
    imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=400&fit=crop',
    username: 'techie_thoughts',
    caption: 'Just shipped a feature I\'ve been working on for weeks. It\'s live and users are already loving it. This is why I code.',
    category: 'Tech',
  },
]

interface VibeTrainingModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function VibeTrainingModal({
  open,
  onClose,
  onSuccess,
}: VibeTrainingModalProps): JSX.Element {
  const [currentStep, setCurrentStep] = useState(0)
  const [comments, setComments] = useState<Record<number, string>>({})
  const [createVibes, { loading }] = useCreateVibesMutation()

  const currentPost = TRAINING_POSTS[currentStep]
  const totalSteps = TRAINING_POSTS.length
  const progress = ((currentStep + 1) / totalSteps) * 100

  const currentComment = comments[currentPost?.id] ?? ''
  const canGoNext = currentComment.trim().length >= 5
  const isLastStep = currentStep === totalSteps - 1
  const completedCount = Object.values(comments).filter((c) => c.trim().length >= 5).length

  const handleCommentChange = (value: string) => {
    setComments((prev) => ({
      ...prev,
      [currentPost.id]: value,
    }))
  }

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSave = async () => {
    const vibeComments = Object.values(comments).filter((c) => c.trim().length >= 5)

    if (vibeComments.length < 5) {
      errorMessage('Please complete at least 5 comments to save your style')
      return
    }

    try {
      const result = await createVibes({
        variables: {
          data: JSON.stringify(vibeComments),
        },
      })

      if (result.data?.createVibes) {
        successMessage('Your comment style has been saved!')
        onSuccess?.()
        handleClose()
      } else {
        errorMessage('Failed to save your style')
      }
    } catch (err) {
      console.error('Error saving vibes:', err)
      errorMessage('Failed to save your style')
    }
  }

  const handleClose = () => {
    setCurrentStep(0)
    setComments({})
    onClose()
  }

  const handleSkip = () => {
    // Clear current comment and move to next
    setComments((prev) => ({
      ...prev,
      [currentPost.id]: '',
    }))
    handleNext()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a2e',
          color: '#fff',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Train Your Comment Style
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
            {currentStep + 1} of {totalSteps} • {completedCount} completed
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <Close />
        </IconButton>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          bgcolor: 'rgba(255,255,255,0.1)',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
          },
        }}
      />

      <DialogContent sx={{ p: 0 }}>
        {/* Post Preview */}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {/* Post Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {currentPost?.username[0].toUpperCase()}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  @{currentPost?.username}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                  {currentPost?.category}
                </Typography>
              </Box>
            </Box>

            {/* Post Image */}
            <Box
              component="img"
              src={currentPost?.imageUrl}
              alt="Post"
              sx={{
                width: '100%',
                height: 250,
                objectFit: 'cover',
                bgcolor: 'rgba(0,0,0,0.3)',
              }}
            />

            {/* Caption */}
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                {currentPost?.caption}
              </Typography>
            </Box>
          </Box>

          {/* Comment Input */}
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
              What would you comment on this post?
            </Typography>
            <TextField
              multiline
              rows={3}
              placeholder="Write your comment..."
              value={currentComment}
              onChange={(e) => handleCommentChange(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
              }}
            />
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, mt: 1 }}>
              Write naturally - this helps the AI learn your authentic voice
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {currentStep > 0 && (
            <Button
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Back
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isLastStep && (
            <Button onClick={handleSkip} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Skip
            </Button>
          )}

          {isLastStep ? (
            <Button
              onClick={() => { void handleSave() }}
              variant="contained"
              disabled={loading || completedCount < 5}
              startIcon={<Check />}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              {loading ? 'Saving...' : `Save Style (${completedCount}/5 min)`}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!canGoNext}
              endIcon={<ArrowForward />}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}
